import { useEffect, useState } from "react";
import api from "@/lib/api";
import { DashboardShell } from "@/components/dashboard/DashboardShell";
import { STUDENT_NAV } from "@/pages/student/StudentHome";
import { SkillRadar } from "@/components/charts/SkillRadar";
import { TsbGauge } from "@/components/charts/TsbGauge";
import { LevelBadge } from "@/components/LevelBadge";
import { skillMeta } from "@/lib/framework";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Compass, Info, GraduationCap, Building2 } from "lucide-react";

const TIPS = {
  tsb: "Your Transferable Skills Benchmark summarizes transferable skills you have demonstrated through approved learning evidence. Exploring careers does not change your TSB.",
  target: "The transferable skills emphasized for this occupation, based on the GPA+ occupational baseline and, when available, aggregated verified employer input.",
  alignment: "Career Alignment compares your demonstrated transferable skills with the skills emphasized for this occupation. It is for career exploration and does not predict hiring or job performance.",
  onet: "A standardized occupational classification (O*NET-SOC) used to organize careers by the type of work performed, so career comparisons stay consistent.",
};

function InfoDot({ tip, id }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="relative inline-block align-middle">
      <button type="button" data-testid={`info-${id}`}
        onClick={() => setOpen((o) => !o)} onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}
        className="ml-1 inline-flex h-4 w-4 items-center justify-center rounded-full bg-slate-200 text-[10px] font-bold text-slate-600 hover:bg-[#005291] hover:text-white">?</button>
      {open && (
        <span className="absolute left-1/2 top-6 z-30 w-60 -translate-x-1/2 rounded-lg bg-[#003057] p-2.5 text-left text-[11px] font-normal leading-snug text-white shadow-xl">{tip}</span>
      )}
    </span>
  );
}

export default function WhatIfPage() {
  const [me, setMe] = useState(null);
  const [occGroups, setOccGroups] = useState([]);
  const [careerId, setCareerId] = useState("");
  const [fit, setFit] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/student/me").then(({ data }) => setMe(data));
    api.get("/occupations").then(({ data }) => {
      const groups = {};
      data.forEach((o) => {
        const g = o.primary_industry || "Other";
        (groups[g] = groups[g] || []).push(o);
      });
      const ordered = Object.keys(groups).sort().map((name) => ({ name, items: groups[name] }));
      setOccGroups(ordered);
      if (data.length) setCareerId(data[0].career_id);
    });
  }, []);

  useEffect(() => {
    if (!careerId) return;
    setLoading(true);
    api.get("/career-target", { params: { career_id: careerId } })
      .then(({ data }) => setFit(data))
      .finally(() => setLoading(false));
  }, [careerId]);

  const radarSeries = [];
  if (fit) {
    radarSeries.push({ key: "you", name: "Your demonstrated skills", color: "#005291", scores: fit.demonstrated });
    radarSeries.push({ key: "target", name: "Career Target Profile", color: "#78be21", scores: fit.career_target, fillOpacity: 0.22 });
  }

  return (
    <DashboardShell title="What-if & Career Fit"
      subtitle="Compare your demonstrated skills to a career's target profile — your TSB never changes"
      nav={STUDENT_NAV}>
      {!me && <div className="flex h-64 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#003057]" /></div>}
      {me && (
        <div className="space-y-6">
          <Card className="p-6 card-elevate">
            <p className="mb-1.5 flex items-center gap-2 text-sm font-bold text-[#003057]"><Compass className="h-4 w-4 text-[#78be21]" /> Explore a career (occupation, grouped by industry)</p>
            <Select value={careerId} onValueChange={setCareerId}>
              <SelectTrigger data-testid="whatif-career-dropdown" className="w-full"><SelectValue placeholder="Choose a career" /></SelectTrigger>
              <SelectContent className="max-h-80">
                {occGroups.map((grp) => (
                  <SelectGroup key={grp.name}>
                    <SelectLabel className="text-[11px] uppercase tracking-wide text-slate-400">{grp.name}</SelectLabel>
                    {grp.items.map((o) => (
                      <SelectItem key={o.career_id} value={o.career_id} data-testid={`whatif-career-${o.career_id}`}>{o.local_title}</SelectItem>
                    ))}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>

            {loading && <div className="mt-6 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#003057]" /></div>}

            {fit && !loading && (
              <div className="mt-6 grid grid-cols-1 items-center gap-4 sm:grid-cols-3" data-testid="whatif-result">
                <div className="text-center">
                  <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Your TSB <InfoDot tip={TIPS.tsb} id="tsb" /></p>
                  <p className="font-mono-num text-3xl font-bold text-[#005291]" data-testid="your-tsb">{fit.your_tsb.toFixed(2)}</p>
                  <LevelBadge level={fit.your_level} />
                  <p className="mt-0.5 text-[10px] text-slate-400">unchanged by exploring</p>
                </div>
                <div className="text-center">
                  <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Selected career</p>
                  <p className="font-heading text-lg font-bold text-[#003057]" data-testid="selected-career">{fit.career.title}</p>
                  <p className="text-[11px] text-slate-400">{fit.career.industry}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Career Alignment <InfoDot tip={TIPS.alignment} id="alignment" /></p>
                  <p className="font-mono-num text-3xl font-bold text-[#78be21]" data-testid="career-alignment">{Math.round(fit.alignment)}%</p>
                  <p className="text-[10px] text-slate-400">exploration only · not a prediction</p>
                </div>
              </div>
            )}
          </Card>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <Card className="p-6 card-elevate">
              <h3 className="mb-1 font-heading text-lg font-bold text-[#003057]">Skill profile overlay</h3>
              <p className="mb-3 text-xs text-slate-500">
                <span className="font-semibold text-[#005291]">Blue</span> = your demonstrated skills ·
                <span className="ml-1 font-semibold text-[#5fa114]">Green</span> = Career Target Profile <InfoDot tip={TIPS.target} id="target" />. Only the green profile changes when you switch careers.
              </p>
              <SkillRadar series={radarSeries} testId="whatif-radar" />
            </Card>
            <Card className="flex flex-col items-center justify-center p-6 card-elevate">
              <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Your benchmark (unchanged)</p>
              <TsbGauge tsb={me.profile.tsb} tsb100={me.profile.tsb_100} size={220} />
            </Card>
          </div>

          {fit && (
            <Card className="p-6 card-elevate" data-testid="about-career">
              <h3 className="font-heading text-lg font-bold text-[#003057]">About this career</h3>
              <p className="mt-1 text-sm text-slate-600">{fit.career.description}</p>
              <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="rounded-lg bg-slate-50 p-3 text-sm">
                  <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Standard occupation <InfoDot tip={TIPS.onet} id="onet" /></p>
                  <p className="mt-0.5 font-semibold text-[#003057]">{fit.career.onet_title}</p>
                  <p className="font-mono-num text-xs text-slate-500">O*NET-SOC: {fit.career.onet_soc || "—"}</p>
                </div>
                <div className="rounded-lg bg-slate-50 p-3 text-sm">
                  <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Career profile</p>
                  <p className="mt-0.5 text-xs text-slate-500">Source: GPA+ occupational baseline (employer input to follow)</p>
                  <p className="mt-1 inline-block rounded bg-amber-100 px-2 py-0.5 text-[11px] font-bold text-amber-800" data-testid="career-source-label">{fit.career.source_label}</p>
                  <p className="mt-1 text-[11px] text-slate-400">Updated {fit.career.updated}</p>
                </div>
              <div className="rounded-lg bg-slate-50 p-3 text-sm">
                  <p className="flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-slate-400"><Building2 className="h-3 w-3" /> Industry (NAICS)</p>
                  {(fit.career.naics || []).length ? (
                    <div className="mt-1 flex flex-wrap gap-1.5" data-testid="career-naics">
                      {fit.career.naics.map((n) => (
                        <span key={n.code} className="inline-flex items-center gap-1 rounded-full bg-[#005291]/10 px-2 py-0.5 text-[11px] font-semibold text-[#005291]">
                          <span className="font-mono-num text-[10px] text-[#005291]/70">{n.code}</span>{n.title}
                        </span>
                      ))}
                    </div>
                  ) : <p className="mt-0.5 text-xs text-slate-400">—</p>}
                  <p className="mt-2 text-[11px] text-slate-400">Standardized industry classification (U.S. Census NAICS).</p>
                </div>
              </div>
              {fit.career.provenance && Object.values(fit.career.provenance).some((c) => (c || []).length) && (
                <details className="mt-4 rounded-lg border border-[#005291]/20 bg-[#005291]/5 p-3" data-testid="career-provenance">
                  <summary className="cursor-pointer text-xs font-bold uppercase tracking-widest text-[#005291]">Sourced from O*NET — how this target was set</summary>
                  <div className="mt-2 space-y-1.5">
                    {Object.entries(fit.career.provenance).map(([sid, contribs]) => (
                      (contribs || []).length > 0 && (
                        <div key={sid} className="text-[11px] text-slate-600">
                          <span className="font-semibold text-[#003057]">{skillMeta(sid)?.name || sid}:</span>{" "}
                          {contribs.map((c) => c.name).join(", ")}
                        </div>
                      )
                    ))}
                  </div>
                  <p className="mt-2 text-[11px] text-slate-400">O*NET skill elements (Importance + Level) mapped to each GPA+ skill. Exploring this never changes your TSB.</p>
                </details>
              )}
              {(fit.career.programs || []).length > 0 && (
                <div className="mt-4 rounded-lg border border-[#78be21]/30 bg-[#78be21]/5 p-3" data-testid="career-programs">
                  <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-[#5fa114]"><GraduationCap className="h-3.5 w-3.5" /> LSC programs that prepare for this occupation</p>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {fit.career.programs.map((p) => (
                      <span key={p.id} className="inline-flex items-center rounded-full bg-white px-2.5 py-0.5 text-[11px] font-semibold text-[#003057] shadow-sm ring-1 ring-[#78be21]/40" data-testid={`career-program-${p.id}`}>{p.name}</span>
                    ))}
                  </div>
                  <p className="mt-2 text-[11px] text-slate-400">Exploring these programs never changes your TSB.</p>
                </div>
              )}
              <div className="mt-4">
                <p className="mb-2 text-xs font-bold uppercase tracking-widest text-slate-400">Skill-by-skill</p>
                <div className="space-y-1.5" data-testid="career-per-skill">
                  {(fit.per_skill || []).map((p) => {
                    const opp = p.gap > 0.15;
                    return (
                      <div key={p.id} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-1.5 text-sm">
                        <span className="font-semibold text-[#003057]">{p.name}</span>
                        <span className="flex items-center gap-3 text-xs">
                          <span className="text-slate-500">You <b className="font-mono-num text-[#005291]">{p.score.toFixed(1)}</b></span>
                          <span className="text-slate-500">Target <b className="font-mono-num text-[#5fa114]">{p.demand.toFixed(1)}</b></span>
                          <span className={`rounded-full px-2 py-0.5 font-semibold ${opp ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"}`}>{opp ? "Development opportunity" : "On target"}</span>
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </Card>
          )}
        </div>
      )}
    </DashboardShell>
  );
}
